import type { ExportJobDetail } from "@/types/api";

const REMARK_MAX = 80;

function normalizeFilterSnapshotRaw(
  job: ExportJobDetail,
): string | null {
  const v = job.filter_snapshot_json;
  if (v == null) {
    return null;
  }
  if (typeof v === "string") {
    const trimmed = v.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  try {
    return JSON.stringify(v);
  } catch {
    return null;
  }
}

/** Wall-clock execution duration (prefers API duration_ms). */
export function formatExportDuration(job: ExportJobDetail): string {
  if (job.duration_ms != null && job.duration_ms >= 0) {
    return formatDurationMs(job.duration_ms);
  }
  if (!job.created_at || !job.finished_at) {
    if (job.status === "PENDING" || job.status === "RUNNING") {
      return "In progress";
    }
    return "—";
  }
  const ms = new Date(job.finished_at).getTime() - new Date(job.created_at).getTime();
  if (!Number.isFinite(ms) || ms < 0) {
    return "—";
  }
  return formatDurationMs(ms);
}

export function formatDurationMs(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  }
  const sec = Math.round(ms / 1000);
  if (sec < 60) {
    return `${sec}s`;
  }
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  if (min < 60) {
    return rem > 0 ? `${min}m ${rem}s` : `${min}m`;
  }
  const hr = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${hr}h ${m}m` : `${hr}h`;
}

const FILTER_EPOCH_KEYS = [
  "created_from",
  "created_to",
  "due_from",
  "due_to",
] as const;

/** UTC calendar date from Stripe filter epoch seconds. */
function epochSecondsToDateString(epoch: number): string {
  const d = new Date(epoch * 1000);
  if (Number.isNaN(d.getTime())) {
    return String(epoch);
  }
  return d.toISOString().slice(0, 10);
}

function transformFilterSnapshotForDisplay(value: unknown): unknown {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return value;
  }
  const obj = value as Record<string, unknown>;
  const out: Record<string, unknown> = { ...obj };
  for (const key of FILTER_EPOCH_KEYS) {
    const v = out[key];
    if (typeof v === "number" && Number.isFinite(v)) {
      out[key] = epochSecondsToDateString(v);
    }
  }
  return out;
}

/** Pretty-printed filter JSON (technical / debug). */
export function formatFilterSnapshotJson(job: ExportJobDetail): {
  pretty: string | null;
} {
  const raw = normalizeFilterSnapshotRaw(job);
  if (!raw) {
    return { pretty: null };
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    const display = transformFilterSnapshotForDisplay(parsed);
    return { pretty: JSON.stringify(display, null, 2) };
  } catch {
    return { pretty: raw };
  }
}

/** Job-level failure summary for list 备注 column. */
export function formatExportListRemark(job: ExportJobDetail): string | null {
  if (job.status !== "FAILED" && job.status !== "CANCELED") {
    return null;
  }
  const text = job.error_message?.trim();
  if (!text) {
    return job.status === "FAILED" ? "Export failed" : null;
  }
  if (text.length <= REMARK_MAX) {
    return text;
  }
  return `${text.slice(0, REMARK_MAX - 1)}…`;
}

export function formatPdfsCell(job: ExportJobDetail): string {
  if (job.status === "FAILED" && job.invoice_count === 0) {
    return "—";
  }
  const base = `${job.invoice_success_count} / ${job.invoice_count}`;
  if (job.invoice_failed_count > 0) {
    return `${base} (${job.invoice_failed_count} failed)`;
  }
  return base;
}
